<script setup>
import { computed, ref, onActivated, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import {
  useStore,
  useStoreGetters,
  useMapGetter,
} from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useCampaignDrip } from 'dashboard/composables/useCampaignDrip';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SmsPartnerAPI from 'dashboard/api/smsPartner';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CampaignList from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignList.vue';
import WhatsAppCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignDialog.vue';
import EditScheduledCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/EditScheduledCampaignDialog.vue';
import ConfirmDeleteCampaignDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/ConfirmDeleteCampaignDialog.vue';
import CampaignProgressDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignProgressDialog.vue';
import SmsPartnerDialog from 'dashboard/components-next/Campaigns/Pages/CampaignPage/SmsPartnerDialog.vue';
import SMSCampaignEmptyState from 'dashboard/components-next/Campaigns/EmptyState/SMSCampaignEmptyState.vue';

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();
const { isAdmin } = useAdmin();

const selectedCampaign = ref(null);
// Modal central (DS Dialog) aberto via ref — botão do topo OU CTA embaixo da lista.
const campaignDialogRef = ref(null);

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);
const hasCampaignLoadError = computed(() => uiFlags.value.fetchError);

const confirmDeleteCampaignDialogRef = ref(null);
const progressDialogRef = ref(null);
const partnerDialogRef = ref(null);

// Parceiro SMS (operadora Shortcode): o disparo só libera quando conectado/validado.
const partner = ref({ configured: false, validated: false });
// 'loading' | 'ready' | 'error'. Carregando e erro NÃO são "não configurado":
// antes a faixa dizia "não configurado" e oferecia Configurar para um parceiro
// já validado, só porque a resposta ainda não tinha chegado ou a rede falhou.
const partnerState = ref('loading');
const loadPartner = async () => {
  // Recarga depois de salvar não pisca a faixa: só a primeira carga mostra o esqueleto.
  if (partnerState.value !== 'ready') partnerState.value = 'loading';
  try {
    const { data } = await SmsPartnerAPI.get();
    partner.value = data || { configured: false, validated: false };
    partnerState.value = 'ready';
  } catch (e) {
    partnerState.value = 'error';
  }
};
// Guarda a carga em andamento: o ?new=1 do Hub precisa esperar o parceiro,
// senão lê validated=false no primeiro mount e pede para validar à toa.
let partnerLoading = null;
const ensurePartnerLoaded = () => {
  if (!partnerLoading) partnerLoading = loadPartner();
  return partnerLoading;
};
const retryPartner = () => {
  partnerLoading = null;
  return ensurePartnerLoaded();
};
onMounted(ensurePartnerLoaded);

const SMSCampaigns = computed(() => getters['campaigns/getSMSCampaigns'].value);
const hasNoSMSCampaigns = computed(
  () => SMSCampaigns.value?.length === 0 && !isFetchingCampaigns.value
);

const { handleStart, handlePause, handleResume, handleCancel, handlePreview } =
  useCampaignDrip();

const handleDelete = campaign => {
  selectedCampaign.value = campaign;
  confirmDeleteCampaignDialogRef.value.dialogRef.open();
};
const handleProgress = campaign => progressDialogRef.value?.open(campaign);
// Editar (rascunho ou agendada): mesmo diálogo de reagendar da página WhatsApp.
const editScheduledCampaignDialogRef = ref(null);
const handleEdit = campaign =>
  editScheduledCampaignDialogRef.value?.open(campaign);
const openPartner = () => partnerDialogRef.value?.open();
// "Inserir saldo": abre o painel da operadora Short Code (recarga de crédito).
const openShortcodePortal = () =>
  window.open('https://app.smsshortcode.com.br/', '_blank', 'noopener');

// Gating: só abre o criar campanha se o parceiro estiver validado.
// Decisão 30: chegando pelo cartão "Nova ação de marketing" (?new=1), o canal
// SMS já entra pronto e o passo 1 não aparece. Por "Nova campanha" dentro de
// Campanhas não vai família, e o passo 1 aparece normalmente.
const SMS_CHANNEL_FAMILY = ['sms'];
const handleCreateClick = async ({ fromHub = false } = {}) => {
  if (partnerState.value === 'loading') await ensurePartnerLoaded();
  if (partnerState.value === 'error') {
    useAlert(t('CAMPAIGN.SMS.PARTNER.LOAD_ERROR'));
    return;
  }
  if (!partner.value.validated) {
    useAlert(t('CAMPAIGN.SMS.PARTNER.VALIDATE_FIRST'));
    openPartner();
    return;
  }
  campaignDialogRef.value?.open(
    fromHub ? { channelFamily: SMS_CHANNEL_FAMILY } : {}
  );
};

// SiteUp (Hub): "Criar" na tela de início de Ações de Marketing chega com
// ?new=1 e já abre o formulário. A query é consumida na chegada (a página
// fica em keep-alive, por isso também em onActivated), para o F5 não reabrir.
const route = useRoute();
const router = useRouter();
// No primeiro mount em keep-alive, onMounted e onActivated chamam os dois antes
// de o replace limpar a query: a trava evita abrir o formulário duas vezes.
let consumingNewQuery = false;
const openFromQuery = async () => {
  // Sem router (specs montam a página sozinha), não há query a consumir.
  if (String(route?.query?.new || '') !== '1' || consumingNewQuery) return;
  consumingNewQuery = true;
  const cleaned = { ...route.query };
  delete cleaned.new;
  router?.replace({ query: cleaned });
  try {
    // Criar é só de admin (o botão do topo já esconde): o link não abre o formulário.
    if (!isAdmin.value) return;
    await ensurePartnerLoaded();
    await handleCreateClick({ fromHub: true });
  } finally {
    consumingNewQuery = false;
  }
};
onMounted(openFromQuery);
onActivated(openFromQuery);
watch(() => route?.query?.new, openFromQuery);
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.SMS.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.SMS.NEW_CAMPAIGN')"
    @click="handleCreateClick"
  >
    <!-- Status do parceiro SMS (Shortcode) -->
    <div
      class="flex items-center justify-between gap-3 p-3 mb-4 rounded-xl bg-n-alpha-2 border border-n-weak"
      data-testid="sms-partner-status"
    >
      <div
        v-if="partnerState === 'loading'"
        class="flex items-center gap-2 text-sm"
        role="status"
      >
        <span
          class="block h-4 w-64 max-w-full rounded bg-n-alpha-3 animate-pulse"
          aria-hidden="true"
        />
        <span class="sr-only">{{ t('CAMPAIGN.SMS.PARTNER.LOADING') }}</span>
      </div>
      <div
        v-else-if="partnerState === 'error'"
        class="flex items-center gap-2 text-sm"
        role="alert"
      >
        <span class="i-ri-error-warning-line text-n-ruby-11" />
        <span class="text-n-slate-11">
          {{ t('CAMPAIGN.SMS.PARTNER.LOAD_ERROR') }}
        </span>
      </div>
      <div v-else class="flex items-center gap-2 text-sm">
        <span
          :class="
            partner.validated
              ? 'i-ri-checkbox-circle-line text-n-teal-11'
              : 'i-ri-error-warning-line text-n-amber-11'
          "
        />
        <span v-if="partner.validated" class="text-n-slate-11">
          {{ t('CAMPAIGN.SMS.PARTNER.CONNECTED') }}
        </span>
        <span v-else class="text-n-slate-11">
          {{ t('CAMPAIGN.SMS.PARTNER.NOT_CONFIGURED') }}
        </span>
      </div>
      <Button
        v-if="partnerState === 'error'"
        data-testid="sms-partner-retry"
        :label="t('CAMPAIGN.RETRY')"
        variant="faded"
        color="slate"
        size="sm"
        icon="i-ri-refresh-line"
        @click="retryPartner"
      />
      <div
        v-else-if="isAdmin && partnerState === 'ready'"
        class="flex items-center gap-2 shrink-0"
      >
        <Button
          v-if="partner.configured"
          :label="t('CAMPAIGN.SMS.PARTNER.ADD_BALANCE')"
          variant="faded"
          color="slate"
          size="sm"
          icon="i-ri-wallet-line"
          @click="openShortcodePortal"
        />
        <Button
          :label="
            partner.configured
              ? t('CAMPAIGN.SMS.PARTNER.EDIT')
              : t('CAMPAIGN.SMS.PARTNER.CONFIGURE')
          "
          variant="faded"
          color="slate"
          size="sm"
          icon="i-ri-settings-line"
          @click="openPartner"
        />
      </div>
    </div>

    <div
      v-if="isFetchingCampaigns"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div
      v-else-if="hasCampaignLoadError"
      class="flex flex-col items-center justify-center gap-3 py-10 text-n-slate-11"
      role="alert"
    >
      <p>{{ t('CAMPAIGN.LOAD_ERROR') }}</p>
      <Button
        data-testid="campaign-retry"
        :label="t('CAMPAIGN.RETRY')"
        variant="faded"
        color="slate"
        size="sm"
        @click="store.dispatch('campaigns/get')"
      />
    </div>
    <template v-else-if="!hasNoSMSCampaigns">
      <CampaignList
        :campaigns="SMSCampaigns"
        @delete="handleDelete"
        @edit="handleEdit"
        @start="handleStart"
        @pause="handlePause"
        @resume="handleResume"
        @cancel="handleCancel"
        @preview="handlePreview"
        @progress="handleProgress"
      />
      <!-- Criar nova campanha logo abaixo das campanhas recentes -->
      <div v-if="partner.validated && isAdmin" class="flex justify-center mt-4">
        <Button
          :label="t('CAMPAIGN.SMS.NEW_CAMPAIGN')"
          icon="i-ri-add-line"
          size="sm"
          @click="handleCreateClick"
        />
      </div>
    </template>
    <SMSCampaignEmptyState
      v-else
      :title="t('CAMPAIGN.SMS.EMPTY_STATE.TITLE')"
      :subtitle="
        isAdmin
          ? t('CAMPAIGN.SMS.EMPTY_STATE.SUBTITLE')
          : t('CAMPAIGN.VIEW_ONLY_EMPTY_HINT')
      "
    />
    <WhatsAppCampaignDialog
      ref="campaignDialogRef"
      :title="t('CAMPAIGN.SMS.PARTNER.DIALOG_TITLE')"
      initial-channel="sms"
    />
    <EditScheduledCampaignDialog ref="editScheduledCampaignDialogRef" />
    <ConfirmDeleteCampaignDialog
      ref="confirmDeleteCampaignDialogRef"
      :selected-campaign="selectedCampaign"
    />
    <CampaignProgressDialog ref="progressDialogRef" />
    <SmsPartnerDialog ref="partnerDialogRef" @saved="loadPartner" />
  </CampaignLayout>
</template>
